/*
 * @format
 */

import React from 'react';
import { Circle, G, Line, Path, Rect, Text } from 'react-native-svg';

import { FACE_DIAMETER, FACE_RADIUS } from './WatchFace';
import { Arrow, MEDIUM } from './DiagramParts';
import { black, primary, white } from '../../colours';

/*
 * The furniture the watch's two editors share. SetpointEditor.kt and
 * PlusHoursEditor.kt are the same screen with a different value in the middle:
 * the dial ring behind, a ＋ at the top and a － at the bottom, and the value
 * itself as the doorway that saves. So the parts are drawn once here rather
 * than once per illustration, and the crown alongside with them.
 *
 * Everything is expressed in the watch's own dp/sp, as WatchFace does, so it can
 * be read straight off the Kotlin. Each part takes the centre of the face it is
 * being drawn around, since the illustrations place their faces differently.
 */
const WEAR_SCREEN_DP = 220;
export const dp = value => (value * FACE_DIAMETER) / WEAR_SCREEN_DP;
export const sp = dp;

// The arc window both editors inherit from the dial.
export const ARC_START_DEG = 130;
export const ARC_SWEEP_DEG = 280;
export const RING_STROKE = dp(4);

// FilledIconButton(size = 38.dp), in both editors.
export const STEP_BUTTON_RADIUS = dp(19);
/*
 * The watch insets both buttons by 14dp. At 12 o'clock that leaves the ＋ all
 * but touching the track, so it is drawn further in; at 6 o'clock the track has
 * its 80° gap and there is nothing to clear, so the － keeps the watch's own
 * inset and stays clear of whatever sits above it.
 */
const PLUS_BUTTON_INSET = dp(22);
const MINUS_BUTTON_INSET = dp(14);
/*
 * The watch sets these in titleMedium (16sp), but a nominal size is not a drawn
 * size: Montserrat's ＋ is 0.44em tall against roughly 0.53em in the watch's own
 * font, so 16 here would come out about a fifth smaller than the device draws
 * it. 20 reproduces the mark rather than the number — and lands on the 20dp the
 * watch would use had the buttons carried icons instead of text.
 */
const STEP_SIZE = sp(20);
// Both glyphs are centred on the maths axis, 0.35em above the baseline (measured
// off the Montserrat outlines), so that is what has to sit on the button's
// middle — a text's own y being its baseline.
const STEP_AXIS = 0.35;
const STEP_FILL = primary(900);

// The type roles the editors' centre column asks for, in the order they stack.
export const LABEL_SIZE = sp(14); // titleSmall
export const VALUE_SIZE = sp(40); // numeralMedium

/*
 * The two ways round a circle centred on [cx, cy]: a point at a radius and
 * bearing, and the arc between two bearings. Shared so a diagram's ring, its
 * markers and the crown's turn arrows are all struck from one centre.
 */
export const dialGeometry = (cx, cy) => {
  const pointAt = (radius, degrees) => {
    const radians = (degrees * Math.PI) / 180;

    return {
      x: cx + radius * Math.cos(radians),
      y: cy + radius * Math.sin(radians),
    };
  };

  const arcPath = (radius, startDeg, endDeg) => {
    const start = pointAt(radius, startDeg);
    const end = pointAt(radius, endDeg);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;

    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  return { pointAt, arcPath };
};

/* Where the ＋ and － sit on a face centred at [cx, cy]. */
export const stepButtonCentresFor = (cx, cy) => ({
  plus: {
    x: cx,
    y: cy - FACE_RADIUS + PLUS_BUTTON_INSET + STEP_BUTTON_RADIUS,
  },
  minus: {
    x: cx,
    y: cy + FACE_RADIUS - MINUS_BUTTON_INSET - STEP_BUTTON_RADIUS,
  },
});

// The crown, sat on the right edge of the case.
const CROWN_WIDTH = dp(15);
const CROWN_HEIGHT = dp(38);
const CROWN_GAP = 8;
const CROWN_RIDGES = 5;

export const crownBoxFor = (cx, cy) => ({
  x: cx + FACE_RADIUS + CROWN_GAP,
  y: cy - CROWN_HEIGHT / 2,
  width: CROWN_WIDTH,
  height: CROWN_HEIGHT,
});

// The turn arrows run on the circle through the crown's own centre, so each
// reads as a turn OF the crown, and start beyond its body — the crown spans
// about 9° either side of centre at this radius — leaving a gap between the two
// rather than one unbroken sweep.
const TURN_RADIUS = FACE_RADIUS + CROWN_GAP + CROWN_WIDTH / 2;
// Started wider than the crown strictly needs so the lower arrow's head also
// clears a leader arriving at the crown below it; both keep the same 42° so the
// pair stays symmetrical.
const TURN_START_DEG = 22;
const TURN_END_DEG = 64;

/* One of the editors' step buttons: a filled disc carrying its glyph. */
export function StepButton({ centre, glyph }) {
  return (
    <G>
      <Circle
        cx={centre.x}
        cy={centre.y}
        r={STEP_BUTTON_RADIUS}
        fill={STEP_FILL}
      />
      <Text
        x={centre.x}
        y={centre.y + STEP_SIZE * STEP_AXIS}
        fill={white}
        fontFamily={MEDIUM}
        fontSize={STEP_SIZE}
        textAnchor="middle">
        {glyph}
      </Text>
    </G>
  );
}

export function Crown({ cx, cy }) {
  const crown = crownBoxFor(cx, cy);
  const ridgeGap = crown.height / (CROWN_RIDGES + 1);

  return (
    <G>
      <Rect
        x={crown.x}
        y={crown.y}
        width={crown.width}
        height={crown.height}
        rx={dp(5)}
        fill={black}
        stroke={white}
        strokeWidth={2}
      />
      {Array.from({ length: CROWN_RIDGES }, (_, index) => (
        <Line
          key={index}
          x1={crown.x + 2}
          y1={crown.y + ridgeGap * (index + 1)}
          x2={crown.x + crown.width - 2}
          y2={crown.y + ridgeGap * (index + 1)}
          stroke={white}
          strokeWidth={1.5}
        />
      ))}
    </G>
  );
}

/* An arc either side of the crown, each ending in an arrowhead: turn it both ways. */
export function TurnArrows({ cx, cy }) {
  const { pointAt, arcPath } = dialGeometry(cx, cy);
  const upper = { start: -TURN_START_DEG, end: -TURN_END_DEG };
  const lower = { start: TURN_START_DEG, end: TURN_END_DEG };

  return (
    <G>
      <Path
        d={arcPath(TURN_RADIUS, upper.end, upper.start)}
        stroke={white}
        strokeWidth={2}
        fill="none"
      />
      <Arrow
        from={pointAt(TURN_RADIUS, upper.end + 12)}
        to={pointAt(TURN_RADIUS, upper.end)}
      />
      <Path
        d={arcPath(TURN_RADIUS, lower.start, lower.end)}
        stroke={white}
        strokeWidth={2}
        fill="none"
      />
      <Arrow
        from={pointAt(TURN_RADIUS, lower.end - 12)}
        to={pointAt(TURN_RADIUS, lower.end)}
      />
    </G>
  );
}
